import { inject, Injectable } from '@angular/core';
import { from, Observable, Subscriber } from 'rxjs';

import { ResourceUpload } from './resource-upload';
import { Logger } from '../logger.service';

import { FirebaseStorageUrlResolver } from './firebase-storage-url-resolver';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { saveAs } from 'file-saver';

import {
  ref,
  getStorage,
  uploadString,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  UploadResult,
  deleteObject,
} from '@angular/fire/storage';


const logger: Logger = new Logger('FirebaseService');

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private http = inject(HttpClient);
  constructor() /*private firestore: Firestore*/ { }

  getBucketName(): string {
    // return environment.configFirebase.storageBucket;
    const storageRef = ref(getStorage()).bucket;
    return storageRef;
  }

  resolveStorageUrl(storageRelativeUrl: string) {
    return new FirebaseStorageUrlResolver(this).resolveUrl(storageRelativeUrl);
  }


  downloadResource(resourceFullPath: string, withCredentials: boolean = true): Observable<any> {
    return new Observable((subscriber: Subscriber<any>) => {
      new FirebaseStorageUrlResolver(this)
        .resolveUrl(resourceFullPath)
        .then((url: any) => {
          const options = (withCredentials) ? {} : { headers: new HttpHeaders({ 'Authorization': '' }) };
          if (url !== '') {
            this.http.get(url, options).subscribe({
              next: (json: any) => {
                subscriber.next(json);
                subscriber.complete();
              },
              error: (error) => {
                logger.error(`Download FAIL: ${error}`);
                subscriber.next(error);
                subscriber.complete();
              }
            });
          } else {
            subscriber.next('');
            subscriber.complete();
          }
        })
        .catch((error) => {
          logger.error(`Download FAIL: ${error}`);
          subscriber.next(error);
          subscriber.complete();
        });
    });
  }

  downloadResourceAsBlob(
    resourceFullPath: string,
    typeBlob: string,
    withCredentials: boolean = true
  ): Observable<any> {
    return new Observable((subscriber: Subscriber<any>) => {
      console.log(`Resource full path: ${resourceFullPath}`);
      new FirebaseStorageUrlResolver(this)
        .resolveUrl(resourceFullPath)
        .then((url: any) => {
          //{headers: this.headers, responseType: ResponseContentType.Blob}


          if (withCredentials) {
            this.http
              .get(url, { responseType: 'blob' })
              .subscribe((response: any) => {
                const blob = new Blob([response], { type: typeBlob });
                subscriber.next(blob);
                subscriber.complete();
              });

          } else {
            this.http
              .get(url, { headers: new HttpHeaders({ 'Authorization': '' }), responseType: 'blob' })
              .subscribe((response: any) => {
                const blob = new Blob([response], { type: typeBlob });
                subscriber.next(blob);
                subscriber.complete();
              });
          }
        })
        .catch((error) => {
          logger.error('Download FAIL: ${error}');
          subscriber.next(error);
          subscriber.complete();
        });
    });
  }

  downloadResourceFromStorageAsBlob(
    url: string,
    typeBlob: string,
    withCredentials: boolean = true
  ): Observable<any> {
    return new Observable((subscriber: Subscriber<any>) => {
      if (withCredentials) {


        this.http.get(url, { responseType: 'blob' }).subscribe({
          next: (response: any) => {
            const blob = new Blob([response], { type: typeBlob });
            subscriber.next(blob);
            subscriber.complete();
          },
          error: (error) => {
            logger.error('Download FAIL: ${error}');
            subscriber.next(error);
            subscriber.complete();
          },
        });
      } else {
        this.http
          .get(url, { headers: new HttpHeaders({ 'Authorization': '' }), responseType: 'blob' }).subscribe({
            next: (response: any) => {
              const blob = new Blob([response], { type: typeBlob });
              subscriber.next(blob);
              subscriber.complete();
            },
            error: (error) => {
              logger.error('Download FAIL: ${error}');
              subscriber.next(error);
              subscriber.complete();
            },
          });

      }
    });
  }

  fsUploadString(upload: any, resourceFullPath: string): Observable<any> {
    // Get a reference to the storage service, which is used to create references in your storage bucket
    // Create a storage reference from our storage service
    const storageRef = ref(getStorage(), resourceFullPath);
    //const ref =  this.storage.ref(resourceFullPath);
    return new Observable((observer: any) => {
      uploadString(storageRef, upload)
        .then((snapshot: any) => {
          // upload success
          logger.info('Upload SUCCESS ' + resourceFullPath);
          observer.next(snapshot.ref);
          observer.complete('ok');
        })
        .catch((error: any) => {
          logger.error(`Upload FAIL: ${error}`);
          observer.next(error);
          observer.complete();
        });
    });
  }

  uploadBase64Promise(
    upload: any,
    resourceFullPath: string,
    contentType: string
  ): Promise<any> {
    const storageRef = ref(getStorage(), resourceFullPath);
    // const ref = this.storage.ref(resourceFullPath);
    const i = upload.indexOf(',');
    if (i >= 0) {
      upload = upload.slice(i + 1);
    }

    return uploadString(storageRef, upload, 'base64', {
      contentType: contentType,
    });
  }

  uploadBase64(
    upload: any,
    resourceFullPath: string,
    contentType: string
  ): Observable<any> {
    const storageRef = ref(getStorage(), resourceFullPath);
    // const ref = this.storage.ref(resourceFullPath);
    const i = upload.indexOf(',');
    if (i >= 0) {
      upload = upload.slice(i + 1);
    }
    return new Observable((observer: any) => {
      uploadString(storageRef, upload, 'base64', { contentType: contentType })
        .then((snapshot: any) => {
          // upload success
          logger.info('Upload SUCCESS ' + resourceFullPath);
          observer.next(snapshot.ref);
          observer.complete('ok');
        })
        .catch((error: any) => {
          logger.error(`Upload FAIL: ${error}`);
          observer.next(error);
          observer.complete();
        });
    });
  }

  uploadBlob(blob: Blob, resourceFullPath: string): Observable<any> {
    return new Observable((observer: any) => {
      uploadBytes(ref(getStorage(), resourceFullPath), blob).then(
        (snapshot: any) => {
          console.log('Uploaded a blob or file! ' + resourceFullPath);
          observer.next(snapshot.ref);
          observer.complete();
        },
        (error: any) => {
          logger.error(`Upload FAIL: ${error}`);
          observer.next(error);
          observer.complete();
        }
      );
    });
  }

  uploadFile(
    upload: ResourceUpload,
    resourceFullPath: string
  ): Observable<any> {
    return new Observable((observer: any) => {
      // const filePath = `${resourceFullPath}/${upload.file.name}`;
      const storageRef = ref(getStorage(), resourceFullPath);
      const uploadTask = uploadBytesResumable(storageRef, upload.file);
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Observe state change events such as progress, pause, and resume
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Upload is ' + progress + '% done');
          switch (snapshot.state) {
            case 'paused':
              console.log('Upload is paused');
              break;
            case 'running':
              console.log('Upload is running');
              break;
          }
          observer.next(progress);
        },
        (error) => {
          // Handle unsuccessful uploads
          observer.error(error);
          observer.complete();
        },
        () => {
          //  Handle successful uploads on complete
          //  For instance, get the download URL: https://firebasestorage.googleapis.com/...
          // getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          //   console.log('File available at', downloadURL);
          observer.next(uploadTask.snapshot.ref);
          observer.complete(uploadTask.snapshot.ref);
          // });
        }
      );
    });
    //return uploadTask.snapshot.bytesTransferred;
  }

  getDownloadUriObs(path: string): Observable<string> {
    const result = from(this.getDownloadUri(path));
    return result;
  }

  getDownloadUri(path: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const storageRef = ref(getStorage(), path);
      getDownloadURL(storageRef)
        .then((url) => {
          resolve(url);
        })
        .catch((error) => {
          logger.error('FireBase Storage NOT FOUND: ' + error.message);
          resolve('');
        });
    });
  }

  deleteResource(path: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const storageRef = ref(getStorage(), path);
      deleteObject(storageRef)
        .then(() => {
          resolve(true);
        })
        .catch((error) => {
          logger.error('FireBase Storage NOT FOUND: ' + error.message);
          resolve(false);
        });
    });
  }

  getDownloadUriAndReparImage(path: string, withCredentials: boolean = true): Promise<string[]> {
    return new Promise((resolve) => {
      this.getDownloadUri(path)
        .then((url: string) => {
          if (url != '') {
            return Promise.resolve([url, '']);
          } else {
            return new FirebaseStorageUrlResolver(this)
              .repairNoImage(path, withCredentials)
              .then((url: string) => {
                return Promise.resolve([url, '']);
              })
              .catch(() => {
                return Promise.resolve(['', '']);
              });
          }
        })
        .then((url: string[]) => {
          return resolve(url);
        });
    });
  }

  uploadStringPromise(
    text: any,
    resourceFullPath: string,
    type: string
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const storageRef = ref(getStorage(), resourceFullPath);
      //const ref = this.storage.ref(resourceFullPath);
      uploadString(storageRef, text, 'raw', { contentType: type })
        .then((snapshot: any) => {
          // upload success
          logger.info('Promise Upload SUCCESS');
          // resolve('ok');
          resolve(storageRef);
        })
        .catch((error: any) => {
          logger.error(`Promise Upload FAIL: ${error}`);
          reject(error);
        });
    });
  }
  downloadChromeLink(so: string, withCredentials: boolean) {
    let fn = 'Google Chrome Touch';
    switch (so) {
      case 'windows':
        fn += '.lnk';
        break;
      case 'linux':
        fn += '.desktop';
        break;
      case 'mac':
        fn += '.sh';
        break;
    }
    this.downloadResourceAsBlob('/assets/links/' + fn, '', withCredentials).subscribe((blob) => {
      saveAs(blob, fn);
    });
  }
  getRef(url: string) {
    // return this.storage.ref(url);
    return ref(getStorage(), url);
  }
}
